import * as v from 'valibot'
import { NovelPlayLanguage } from './play-config.js'
import { NovelGenres, NovelSynopsis, NOVEL_STOREFRONT_LOCALES } from './storefront.js'

const Id = v.pipe(v.string(), v.minLength(1))

/**
 * 书架卡片里的人物：不含作品包内 player_id 对应的人物；image 为空表示该人物没有配图。
 * @typedef {{ id: string, name: string, image: string|null }} NovelCharacterT
 */
export const NovelCharacter = v.object({
  id: Id,
  name: v.string(),
  image: v.nullable(v.string()),
})

/**
 * 书架上的一部已上架作品，字段取作品当前内容。teaser 取店面译文的钩子，原文语言没有店面版本时取开场前 120 字；featured_rank 为空表示非精选；
 * listed_at 是最近一次上架时间（ISO 8601）。
 * storefront_language 是本次响应的店面语言；null 表示该语言的店面内容尚未准备好。language 始终为游玩正文语言。
 * @typedef {{ id: string, slug: string, title: string, language: string, storefront_language: string|null, cover_url: string|null, teaser: string, genres: string[], characters: NovelCharacterT[], featured_rank: number|null, listed_at: string }} NovelCardT
 */
export const NovelCard = v.object({
  id: Id,
  slug: Id,
  title: v.string(),
  language: Id,
  play_languages: v.array(NovelPlayLanguage),
  storefront_language: v.nullable(v.picklist(NOVEL_STOREFRONT_LOCALES)),
  cover_url: v.nullable(v.string()),
  teaser: v.pipe(v.string(), v.maxLength(120)),
  genres: NovelGenres,
  characters: v.array(NovelCharacter),
  featured_rank: v.nullable(v.pipe(v.number(), v.integer(), v.minValue(0))),
  listed_at: v.string(),
})

/**
 * GET /api/novel/catalog：精选按运营设置的排序值升序；最新含全部已上架作品，按最近一次上架时间倒序。
 * @typedef {{ featured: NovelCardT[], latest: NovelCardT[] }} NovelCatalogT
 */
export const NovelCatalog = v.object({
  featured: v.array(NovelCard),
  latest: v.array(NovelCard),
})

/**
 * 详情页的人物：public_profile 是玩家可见的公开介绍。
 * @typedef {{ id: string, name: string, public_profile: string, romanceable: boolean, image: string|null }} NovelCharacterProfileT
 */
export const NovelCharacterProfile = v.object({
  id: Id,
  name: v.string(),
  public_profile: v.string(),
  romanceable: v.boolean(),
  image: v.nullable(v.string()),
})

/**
 * GET /api/novel/packs/:slug：卡片加人物简介；作品非 published 时接口返回 NOT_FOUND，不会有这个形状。
 * Only the opening location is public here. No author rules, private profiles, facts or relations are returned.
 * @typedef {NovelCardT & { synopsis: string, opening_location: { name: string, image: string|null }|null, character_profiles: NovelCharacterProfileT[] }} NovelPackDetailT
 */
export const NovelPackDetail = v.object({
  ...NovelCard.entries,
  synopsis: NovelSynopsis,
  opening_location: v.nullable(v.object({ name: v.string(), image: v.nullable(v.string()) })),
  character_profiles: v.array(NovelCharacterProfile),
})
